/**
 * App.tsx — THE PLATFORM CONSOLE, WHOLE.
 *
 * ===========================================================================
 * PLACEMENT: WHY THIS IS AN APPLICATION AND NOT A SCREEN
 * ===========================================================================
 * Founder ruling 4, 2026-09-08: "Curl is not the finished operational product…
 * It is not a customer Admin hub and must never be reachable through tenant
 * authority."
 *
 * The six-hub IA in `apps/admin` is locked and a seventh hub is forbidden, so
 * the question was where a platform-only surface can live. There is no answer
 * inside `apps/admin`, and the reason is mechanical rather than aesthetic:
 * `apps/admin/test/unit/route-reachability.test.ts` requires EVERY route
 * `App.tsx` registers to have a rail entry or an inbound link from another
 * admin screen. Both ways in are tenant IA. A console route there would have to
 * be a seventh hub (forbidden), a card inside one of the six (which teaches
 * every customer administrator that publishing external law is a tenant
 * capability someone could be granted — the exact merge the two-authority
 * ruling forbids), or an entry in DELIBERATE_ALIASES, whose own guard demands
 * the reason name a rail destination that is reachable instead. There is none.
 *
 * And even if the guard could be satisfied: `apps/admin` is SERVED TO TENANT
 * BROWSERS under a tenant session cookie. Shipping the console inside that
 * bundle makes it tenant-reachable by construction — the route table, the
 * screens and the request shapes all arrive in the customer's browser. Not
 * being in the menu is not the same as not being reachable.
 *
 * So: `apps/console`, its own Vite application, its own origin, its own build,
 * no shared cookie, no `@cwos/api-client`. That is not a new idea — ADR 0015 §2
 * decided it in August and named this directory. This is its first surface.
 *
 * ===========================================================================
 * THE SMALLEST SURFACE: THREE ACTS AND FOUR READS
 * ===========================================================================
 * "Smallest necessary" is a ceiling, so the omissions are as much the design as
 * the inclusions.
 *
 * ACTS — the four the ruling named, in three calls:
 *   REVIEW     approve or reject a proposal, with the finding recorded
 *   PUBLISH    turn an approval into an immutable version
 *   SUPERSEDE  NOT a fourth call. A proposal carries `supersedesRuleId` from
 *              extraction; publishing it IS the supersede. What the console
 *              adds is that the operator SEES what closes before they do it.
 *   RETIRE     withdraw a version, with the day it stopped and the reason
 *
 * READS — each one is what makes an act honest rather than a rubber stamp:
 *   the corpus (what is in force, and what happened to it)
 *   the queue (what is waiting)
 *   the source document (source, provenance, and the declared/verified split)
 *   the source-class catalog (why a source may or may not become a floor)
 *
 * ===========================================================================
 * WHAT IS DELIBERATELY NOT HERE
 * ===========================================================================
 *   INGEST and PROPOSE. The lifecycle begins with them and neither is one of
 *     the four verbs. A source-text paste box up to 2 MB with a SHA-256 field
 *     would have been the single largest thing in this console, built for the
 *     act the ruling did not ask for. CONSEQUENCE, STATED RATHER THAN HIDDEN:
 *     documents and proposals still arrive by curl or by the source-change
 *     pipeline. The console is where they are decided, not where they are
 *     created.
 *   WITHDRAW A PROPOSAL. `POST /proposals/:id/withdrawal` exists. Rejecting
 *     with a recorded finding is the reviewed version of the same outcome and
 *     it is inside REVIEW; withdrawal is not one of the four.
 *   SOURCE-CHANGE (observe / diff / propose / review). Left out on purpose —
 *     see the lane report. It has no HTTP door at all, so surfacing it means
 *     building six routes first, and its accepted proposals cannot become
 *     publications: there is no bridge from `global_source_change_proposal` to
 *     `global_rule_proposal`. A console tab over it would end in a queue whose
 *     items have no next step. It belongs here the day both exist, and the
 *     placement this file establishes is where it goes.
 *   EDIT and DELETE. This domain has neither and never will: a published
 *     requirement is immutable, a proposal's substance is frozen at insert, a
 *     document's text is frozen at ingest.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PlatformActor } from './platform/credentials.js';
import { consoleAdmission } from './platform/authority.js';
import type { PresentedPrincipal } from './platform/authority.js';
import { CONSOLE_GROUPS, CONSOLE_SECTIONS } from './platform/sections.js';
import type { ConsoleSectionId } from './platform/sections.js';
import { createConsoleClient } from './platform/client.js';
import { isStaffToken, staffRequests } from './platform/staffSession.js';
import type { ConsoleOutcome } from './platform/client.js';
import * as requests from './platform/requests.js';
import type {
  CorpusResponse,
  QueueResponse,
  Requirement,
  RuleProposal,
  SourceClass,
  SourceClassesResponse,
} from './platform/types.js';
import { OperatorGate } from './screens/OperatorGate.js';
import { ReviewQueue } from './screens/ReviewQueue.js';
import { Corpus } from './screens/Corpus.js';
import { CustomersArea } from './screens/Customers.js';
import { Logs } from './screens/Logs.js';
import { ScreenBoundary } from './screens/ScreenBoundary.js';
import { AiIntelligence } from './screens/AiIntelligence.js';
import { Analytics } from './screens/Analytics.js';
import { StaffAccess } from './screens/StaffAccess.js';
import {
  AdoptionBoard,
  IntegrationsBoard,
  Deployments,
  NotConnected,
  OnboardingBoard,
  Overview,
  SystemHealth,
} from './screens/Sections.js';

/**
 * ABSOLUTE, from the environment, with no same-origin fallback.
 *
 * A relative default (`''`) would make the console call whatever host served
 * it, which is the one thing the placement decision forbids. An unset variable
 * is a configuration error and says so.
 */
export const API_BASE = (import.meta.env.VITE_PLATFORM_API_URL as string | undefined) ?? '';

export function App() {
  /**
   * WHAT WAS PRESENTED, AND SEPARATELY WHETHER IT IS ADMITTED.
   *
   * The two were one variable until the customers area landed, and collapsing
   * them is what made the console's own admission decision unfalsifiable: if
   * the only state is `actor | null`, then "admitted" is spelled "non-null"
   * and there is no shape a test can hand this component that it would refuse
   * for a reason it can name. `consoleAdmission` decides, default-deny, in one
   * place, and `platform/authority.ts` says why the tenant branch is first.
   */
  const [presented, setPresented] = useState<PresentedPrincipal>({ kind: 'none' });
  const [signOutProblem, setSignOutProblem] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const admission = consoleAdmission(presented);
  const actor: PlatformActor | null = admission.admitted ? admission.actor : null;
  const [area, setArea] = useState<ConsoleSectionId>('overview');
  const client = useMemo(() => createConsoleClient({ baseUrl: API_BASE }), []);

  const [proposals, setProposals] = useState<RuleProposal[] | null>(null);
  const [corpus, setCorpus] = useState<Requirement[] | null>(null);
  const [classes, setClasses] = useState<SourceClass[] | null>(null);
  /**
   * ONE LOAD PROBLEM, RENDERED. Not swallowed into an empty list: a console
   * that shows "no proposals waiting" because the request failed has told the
   * operator a measured zero where it had refused-for-scope or a transport
   * failure.
   */
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(
    async (current: PlatformActor) => {
      setLoading(true);
      const [q, c, s] = await Promise.all([
        client<QueueResponse>(requests.queue(), current),
        client<CorpusResponse>(requests.corpus(), current),
        client<SourceClassesResponse>(requests.sourceClasses(), current),
      ]);
      setLoading(false);
      const failed = [q, c, s].find((r) => r.kind !== 'ok') as
        Exclude<ConsoleOutcome<unknown>, { kind: 'ok' }> | undefined;
      if (failed) {
        setLoadProblem(failed.message);
        // The lists are set to null, NOT to []. Null is "we did not learn"; []
        // would be "there are none", and they are different answers.
        setProposals(null);
        setCorpus(null);
        setClasses(null);
        return;
      }
      setLoadProblem(null);
      setProposals((q as { kind: 'ok'; value: QueueResponse }).value.proposals);
      setCorpus((c as { kind: 'ok'; value: CorpusResponse }).value.requirements);
      setClasses((s as { kind: 'ok'; value: SourceClassesResponse }).value.sourceClasses);
    },
    [client],
  );

  useEffect(() => {
    // The four global-knowledge reads fire only when that area is open. Loading
    // them behind the customers tab would knock on the publication door with a
    // tenancy credential and render a 403 nobody asked for.
    if (actor && area === 'knowledge') void refresh(actor);
  }, [actor, area, refresh]);

  if (!admission.admitted) {
    return (
      <main className="gate">
        <div className="gate-inner">
          <Header />
          {signOutProblem ? (
            <div className="banner" role="alert">
              {signOutProblem}
            </div>
          ) : null}
          <OperatorGate
            onPresented={setPresented}
            apiBase={API_BASE}
            /* `no_credential_presented` is the opening state, not a refusal to
             report. Rendering it as one would greet every operator with an
             alarm banner and teach them to ignore the banner. */
            refusal={admission.reason === 'no_credential_presented' ? null : admission.message}
          />
        </div>
      </main>
    );
  }

  const current = CONSOLE_SECTIONS.find((s) => s.id === area) ?? CONSOLE_SECTIONS[0]!;
  const signOut = () => {
    // A staff session is revoked on the server; a secret is only forgotten.
    const actorNow = admission.actor;
    setPresented({ kind: 'none' });
    if (isStaffToken(actorNow.systemToken)) {
      // Awaited and reported (review L5): a revoke that did not reach the
      // server leaves a live session until it idles out, and the person
      // signing out should know that.
      void client(staffRequests.signOut(), actorNow).then((r) =>
        setSignOutProblem(
          r.kind === 'ok'
            ? null
            : `Sign-out did not reach the server (${r.message}). This tab has forgotten the ` +
                'session; on the server it ends after 30 minutes idle.',
        ),
      );
    }
  };

  return (
    <div className="shell" data-nav-open={navOpen ? 'true' : 'false'}>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <aside className="rail" aria-label="Phibian Console navigation">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <img src="/brand/phibian-mark.svg" alt="" />
          </span>
          <span>
            Phibian Console
            <small>Internal operations</small>
          </span>
        </div>
        <nav aria-label="Sections">
          {CONSOLE_GROUPS.map((group) => (
            <div className="nav-group" key={group} role="group" aria-label={group}>
              <div className="group-label" aria-hidden="true">
                {group}
              </div>
              {CONSOLE_SECTIONS.filter((s) => s.group === group).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="nav-item"
                  aria-current={a.id === area ? 'page' : undefined}
                  data-status={a.status}
                  onClick={() => {
                    setArea(a.id);
                    setNavOpen(false);
                  }}
                >
                  <span>{a.label}</span>
                  {a.status === 'not_connected' ? (
                    <span className="nav-pending" title="No data source connected yet">
                      soon
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      {navOpen ? (
        <button
          type="button"
          className="scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <header className="topbar">
        <button
          type="button"
          className="menu-button small"
          aria-expanded={navOpen}
          aria-label="Open navigation"
          onClick={() => setNavOpen((o) => !o)}
        >
          Menu
        </button>
        <h1 className="topbar-title">{current.label}</h1>
        <div className="identity">
          <strong>{admission.actor.operatorId}</strong>
          <span>
            {isStaffToken(admission.actor.systemToken)
              ? 'Two-step verified · ends after 30 min idle or 8 h'
              : 'Operator credential · attribution only'}
          </span>
        </div>
        <button type="button" className="small" onClick={signOut}>
          {isStaffToken(admission.actor.systemToken) ? 'Sign out' : 'Forget credential'}
        </button>
      </header>

      <main className="main" id="main" tabIndex={-1}>
        <p className="page-intro">{current.purpose}</p>
        {signOutProblem ? (
          <div className="banner" role="alert">
            {signOutProblem}
          </div>
        ) : null}

        {/*
        TWO AREAS, TWO CREDENTIALS, AND THE CONSOLE DOES NOT PRETEND OTHERWISE.
        `PLATFORM_TENANCY_OPERATORS` and `PLATFORM_PUBLICATION_OPERATORS` are
        separate secrets holding separate capabilities, and founder ruling 6
        requires them to stay separate. One credential therefore opens one area
        and is answered 403 by the other, with the server naming the capability
        it does hold. The console renders that refusal rather than hiding the
        area, because "the tab is empty" and "your credential is for the other
        authority" send an operator in opposite directions.
      */}
        <ScreenBoundary name={area}>
          {area === 'overview' ? <Overview actor={admission.actor} client={client} /> : null}
          {area === 'organizations' ? (
            <CustomersArea presented={presented} client={client} />
          ) : null}
          {area === 'onboarding' ? (
            <OnboardingBoard actor={admission.actor} client={client} />
          ) : null}
          {area === 'adoption' ? <AdoptionBoard actor={admission.actor} client={client} /> : null}
          {area === 'integrations' ? (
            <IntegrationsBoard actor={admission.actor} client={client} />
          ) : null}
          {area === 'health' ? <SystemHealth actor={admission.actor} client={client} /> : null}
          {area === 'deployments' ? <Deployments actor={admission.actor} client={client} /> : null}
          {area === 'logs' ? <Logs actor={admission.actor} client={client} /> : null}
          {area === 'intelligence' ? (
            <AiIntelligence actor={admission.actor} client={client} />
          ) : null}
          {area === 'analytics' ? <Analytics actor={admission.actor} client={client} /> : null}
          {area === 'access' ? <StaffAccess actor={admission.actor} client={client} /> : null}
        </ScreenBoundary>
        {CONSOLE_SECTIONS.filter((s) => s.id === area && s.status === 'not_connected').map((s) => (
          <NotConnected key={s.id} section={s} />
        ))}

        {area !== 'knowledge' ? null : (
          <>
            {loadProblem ? (
              <div className="banner" role="alert">
                <strong>The console could not load.</strong> {loadProblem}
                <p className="quiet">
                  Nothing below is being shown as empty on account of this — the lists are withheld,
                  not measured as zero.
                </p>
              </div>
            ) : null}

            {loading ? <p className="quiet">Loading…</p> : null}

            <ReviewQueue
              proposals={proposals}
              corpus={corpus}
              classes={classes}
              actor={admission.actor}
              client={client}
              onChanged={() => void refresh(admission.actor)}
            />

            <Corpus
              corpus={corpus}
              actor={admission.actor}
              client={client}
              onChanged={() => void refresh(admission.actor)}
            />
          </>
        )}
      </main>
    </div>
  );
}

function Header() {
  return (
    <>
      <div className="gate-brand">
        <span className="brand-mark" aria-hidden="true">
          <img src="/brand/phibian-mark.svg" alt="" />
        </span>
        Phibian Console
      </div>
      <p className="quiet">
        Phibian&apos;s internal control plane. It is not a customer Admin hub: it holds no tenant
        session, and no tenant role — org_admin included — can reach it.
      </p>
    </>
  );
}
